"use client";

import markdownToHtml from "@/utils/htmlFromatter";
import Image from "next/image";
import { useEffect, useRef, useState } from "react";

interface AccommodationImage {
  title: string;
  description: string;
  url: string;
  thumbs?: {
    sm_q?: string;
    xl?: string;
  };
}
interface AccommodationProps {
  images: AccommodationImage[];
}

export default function Accommodation({ images }: AccommodationProps) {
  const [selectedTitle, setSelectedTitle] = useState<string | null>(null);
  const carouselRef = useRef<HTMLDivElement | null>(null);
  const [scrollPosition, setScrollPosition] = useState(0);
  const [showArrows, setShowArrows] = useState(false);

  const groupedImages = images.reduce<Record<string, AccommodationImage[]>>(
    (acc, img: AccommodationImage) => {
      if (!acc[img.title]) acc[img.title] = [];
      acc[img.title].push(img);
      return acc;
    },
    {},
  );

  const mainImages = images.filter((img) => img.description !== "Bez popisu");

  const scrollImages = (direction: 1 | -1) => {
    if (!carouselRef.current) return;
    const gap = 16;
    const firstChild = carouselRef.current?.children[0] as
      | HTMLElement
      | undefined;
    const cardWidth = firstChild ? firstChild.offsetWidth + gap : 0;
    const maxScrollLeft =
      carouselRef.current.scrollWidth - carouselRef.current.clientWidth;

    if (direction === 1) {
      if (carouselRef.current.scrollLeft >= maxScrollLeft - 1) {
        carouselRef.current.scrollTo({ left: 0, behavior: "smooth" });
      } else {
        carouselRef.current.scrollBy({ left: cardWidth, behavior: "smooth" });
      }
    } else {
      if (carouselRef.current.scrollLeft <= 1) {
        carouselRef.current.scrollTo({
          left: maxScrollLeft,
          behavior: "smooth",
        });
      } else {
        carouselRef.current.scrollBy({ left: -cardWidth, behavior: "smooth" });
      }
    }
  };

  useEffect(() => {
    const checkOverflow = () => {
      if (!carouselRef.current) return;
      const hasOverflow =
        carouselRef.current.scrollWidth > carouselRef.current.clientWidth;
      setShowArrows(hasOverflow);
    };

    checkOverflow();
    window.addEventListener("resize", checkOverflow);
    return () => window.removeEventListener("resize", checkOverflow);
  }, [mainImages.length]);

  useEffect(() => {
    const handleScroll = () => {
      if (!carouselRef.current) return;
      const firstChild = carouselRef.current?.children[0] as
        | HTMLElement
        | undefined;
      const cardWidth = firstChild ? firstChild.offsetWidth + 16 : 0;
      const index = Math.round(carouselRef.current.scrollLeft / cardWidth);
      setScrollPosition(index);
    };

    const ref = carouselRef.current;
    if (ref) ref.addEventListener("scroll", handleScroll);

    return () => {
      if (ref) ref.removeEventListener("scroll", handleScroll);
    };
  }, []);

  const justifyClass = mainImages.length < 3 ? "justify-center" : "";

  return (
    <div className='w-full mt-16 mb-10 text-center relative'>
      <h3 className='font-kalam font-bold text-5xl mb-10'>Ubytovanie</h3>

      {/* Navigation Arrows */}
      {showArrows && (
        <button
          onClick={() => scrollImages(-1)}
          className='absolute -left-15 top-1/2 transform -translate-y-1/2 hidden md:flex w-10 h-10 bg-[#A3A3A3] cursor-pointer hover:bg-black bg-opacity-50 text-white rounded-full items-center justify-center transition hover:bg-opacity-80'
        >
          <svg
            xmlns='http://www.w3.org/2000/svg'
            width='24'
            height='24'
            fill='none'
            stroke='currentColor'
            strokeWidth='2'
            strokeLinecap='round'
            strokeLinejoin='round'
          >
            <polyline points='15 18 9 12 15 6' />
          </svg>
        </button>
      )}

      {showArrows && (
        <button
          onClick={() => scrollImages(1)}
          className='absolute -right-15 z-50 top-1/2 transform -translate-y-1/2 hidden md:flex w-10 h-10 bg-[#A3A3A3] cursor-pointer hover:bg-black bg-opacity-50 text-white rounded-full items-center justify-center transition hover:bg-opacity-80'
        >
          <svg
            xmlns='http://www.w3.org/2000/svg'
            width='24'
            height='24'
            fill='none'
            stroke='currentColor'
            strokeWidth='2'
            strokeLinecap='round'
            strokeLinejoin='round'
          >
            <polyline points='9 18 15 12 9 6' />
          </svg>
        </button>
      )}

      {/* Horizontal Scrollable List */}
      <div
        ref={carouselRef}
        className={`flex gap-4 overflow-x-auto scroll-smooth no-scrollbar ${justifyClass}`}
      >
        {mainImages.map((image: AccommodationImage, index: number) => (
          <div
            key={index}
            className='cursor-pointer shrink-0 w-full md:w-[calc((100%-16px)/2)] lg:w-[calc((100%-32px)/3)]'
            onClick={() => setSelectedTitle(image.title)}
          >
            <div className='relative group overflow-hidden rounded-lg'>
              <Image
                src={image.thumbs?.sm_q ?? image.url}
                alt={image.title}
                width={300}
                height={200}
                className='h-50 shadow-md w-full object-cover transition-transform duration-300 ease-in-out group-hover:scale-105'
                unoptimized
              />
              <div className='absolute bottom-2 right-2 pointer-events-none'>
                <svg
                  xmlns='http://www.w3.org/2000/svg'
                  className='w-5 h-5 text-white drop-shadow-lg'
                  fill='none'
                  viewBox='0 0 24 24'
                  stroke='currentColor'
                >
                  <path
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    strokeWidth='3'
                    d='M11 19a8 8 0 100-16 8 8 0 000 16zm6-2l4 6'
                  />
                </svg>
              </div>
            </div>
            <h4 className='mt-2 font-bold text-left mb-2 text-[#00638A] text-xl'>
              {image.title}
            </h4>
            <div className='text-left mb-4'>
              {markdownToHtml(image.description)}
            </div>
          </div>
        ))}
      </div>

      <div className='flex justify-center md:hidden mt-3'>
        {mainImages.map((_, index: number) => (
          <div
            key={index}
            className={`w-2.5 h-2.5 mx-1 rounded-full transition ${
              scrollPosition === index
                ? "bg-gray-600 w-3 h-3"
                : "bg-gray-400 opacity-50"
            }`}
          />
        ))}
      </div>

      {selectedTitle && (
        <div
          className='fixed inset-0 bg-gray-300/50 flex items-center justify-center p-4'
          onClick={() => setSelectedTitle(null)}
        >
          <div className='relative bg-[#f5f5f5] p-6 rounded-lg max-w-[90vw] max-h-[90vh] overflow-auto shadow-lg'>
            <button
              className='absolute top-4 right-4 text-gray-800 bg-gray-300 w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-400 transition'
              onClick={() => setSelectedTitle(null)}
            >
              &times;
            </button>

            <h3 className='text-gray-800 text-3xl font-bold mb-4 text-center'>
              {selectedTitle}
            </h3>
            <div className='flex gap-4 items-center justify-center'>
              {groupedImages[selectedTitle].map(
                (img: AccommodationImage, idx: number) => (
                  <div key={idx} className='relative w-full h-full'>
                    <Image
                      src={img.url}
                      alt={img.title}
                      width={300}
                      height={200}
                      className='rounded-md h-50 shadow-md object-cover cursor-pointer'
                      onClick={(e) => e.stopPropagation()}
                      unoptimized
                    />
                  </div>
                ),
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
